import { clone } from '@jack-henry/frontend-utils/functions';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { TmBaseComponent } from '@treasury/presentation';
import { SelectItemVm, TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/tm-checkbox';
import '@treasury/presentation/components/tm-currency-range';
// eslint-disable-next-line no-duplicate-imports
import type { CurrencyRange } from '@treasury/presentation/components/tm-currency-range';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import '@treasury/presentation/components/tm-radio-group';
import '@treasury/presentation/components/tm-text-field';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { AchRecipientActivityFilterVm } from '../data/ach-recipient-activity-filter-vm';

const recordTypeOptions: SelectItemVm<string>[] = [
    { label: 'Both', value: 'Both' },
    { label: 'Template', value: 'Batch' },
    { label: 'Payment', value: 'Payment' },
];

const defaultState = {
    amount: true,
    recipientName: false,
};

export const tagName = 'ach-recipient-filter-sheet';
@customElement(tagName)
export class AchRecipientFilterSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public loading = true;

    @property({ type: Boolean })
    public open = false;

    @property({ type: Object })
    public initial?: AchRecipientActivityFilterVm;

    @state()
    private filter: AchRecipientActivityFilterVm = new AchRecipientActivityFilterVm();

    @state()
    private recordType!: string;

    @state()
    private recipientName!: string;

    @state()
    private amountRange!: CurrencyRange;

    @state()
    private prenote!: boolean;

    @state()
    private hold!: boolean;

    @state()
    private reversal!: boolean;

    @DeepReactive()
    private validState = defaultState;

    protected async firstUpdated() {
        if (this.initial) {
            this.filter = clone(this.initial);
        }
        this.unpackFilter();
        this.loading = false;
    }

    protected render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            <p slot="header-center">Filter ACH Recipient Activity</p>
            <p slot="header-right"></p>
            <tm-section class="p-3 mb-10">${this.renderForm()}</tm-section>
            <tm-footer
                .buttonConfig=${[
                    {
                        text: 'Apply Filters',
                        onClick: () => {
                            this.onApplyFilters();
                        },
                        disabled: Object.values(this.validState).some(isValid => !isValid),
                    },
                    {
                        importance: 'secondary',
                        text: 'Reset',
                        onClick: () => {
                            this.onResetForm();
                        },
                    },
                ] as ButtonConfig[]}
            >
            </tm-footer>
        </tm-bottom-sheet> `;
    }

    private onApplyFilters() {
        this.packFilter();
        this.dispatchEvent(
            new CustomEvent('applyFilters', {
                detail: this.filter,
                bubbles: true,
                composed: true,
            })
        );
        this.dispatchEvent(new CustomEvent('close'));
    }

    private onResetForm() {
        this.filter.reset();
        this.unpackFilter();
        this.validState = { ...defaultState };
    }

    private unpackFilter() {
        this.recordType = this.filter.recordType;
        this.recipientName = this.filter.recipientName;
        this.amountRange = this.filter.amountRange;
        this.prenote = this.filter.prenote;
        this.hold = this.filter.hold;
        this.reversal = this.filter.reversal;
    }

    private packFilter() {
        this.filter.recordType = this.recordType;
        this.filter.recipientName = this.recipientName;
        this.filter.amountRange = this.amountRange;
        this.filter.prenote = this.prenote;
        this.filter.hold = this.hold;
        this.filter.reversal = this.reversal;
    }

    private renderForm() {
        if (this.loading || !this.filter) {
            return html`<tm-loader card></tm-loader>`;
        }

        return html` <tm-radio-group
                label="Record Type"
                .initial=${this.recordType}
                .options=${recordTypeOptions}
                @value-changed=${(e: CustomEvent) => {
                    this.recordType = e.detail.value;
                }}
            ></tm-radio-group
            ><tm-text-field
                label="Recipient Name"
                required
                .initial=${this.recipientName}
                @value-changed=${({ detail }: CustomEvent) => {
                    this.recipientName = detail.value;
                    this.validState.recipientName = detail.isValid;
                }}
            ></tm-text-field>
            <tm-currency-range
                .initial=${this.amountRange}
                @value-changed=${(e: TmValueChanged<CurrencyRange>) => {
                    const { isValid, value } = e.detail;
                    this.validState.amount = isValid;
                    if (!value) {
                        return;
                    }
                    this.amountRange = value;
                }}
            ></tm-currency-range>
            <tm-checkbox
                label="Prenote"
                .checked=${this.prenote}
                @checked-changed=${(e: CustomEvent) => {
                    this.prenote = e.detail.value;
                }}
            ></tm-checkbox>
            <tm-checkbox
                label="Hold"
                .checked=${this.hold}
                @checked-changed=${(e: CustomEvent) => {
                    this.hold = e.detail.value;
                }}
            ></tm-checkbox>
            <tm-checkbox
                label="Reversal"
                .checked=${this.reversal}
                @checked-changed=${(e: CustomEvent) => {
                    this.reversal = e.detail.value;
                }}
            ></tm-checkbox>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AchRecipientFilterSheet;
    }
}
